import { useState } from 'react';
import { Copy, Check, FileDown, Loader2, Share2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../components/ui/sonner';
import { renderStudioReference } from '../concepts/renderStudioReference';
import { buildSpecSheet } from './specSheetPdf';
import { designTitle, shareLink } from './designSummary';
import './share.css';

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); }
  catch { const input = document.createElement('textarea'); input.value = text; document.body.appendChild(input); input.select(); document.execCommand('copy'); input.remove(); }
}

export function ShareDialog({ open, onOpenChange, id, design, product, finish }) {
  const [copied, setCopied] = useState(false), [building, setBuilding] = useState(false);
  if (!id || !design) return null;
  const link = shareLink(id), title = designTitle(design, product);
  const copy = async () => { await copyText(link); setCopied(true); toast.success('Link copied'); setTimeout(() => setCopied(false), 2000); };
  const nativeShare = () => navigator.share({ title: `ViewLux · ${title}`, text: `My ViewLux design: ${title}`, url: link }).catch(() => {});
  const downloadPdf = async () => {
    setBuilding(true);
    try {
      const { blob } = await renderStudioReference(design, product, finish, 'plain');
      const pdf = await buildSpecSheet({ design, product, finish, link, image: blob });
      pdf.save(`viewlux-${design.model_id}-${id.slice(0, 8)}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error(err.message || 'The PDF could not be created.'); } finally { setBuilding(false); }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="share-dialog" data-testid="share-dialog">
      <DialogHeader><DialogTitle data-testid="share-dialog-title">Share your design</DialogTitle><DialogDescription>{title}. Anyone with the link opens their own editable copy — your house photo is never shared.</DialogDescription></DialogHeader>
      <label className="share-label" htmlFor="share-link-input">Design link</label>
      <div className="share-link-row"><input id="share-link-input" data-testid="share-link-input" readOnly value={link} onFocus={e => e.target.select()} /><button data-testid="share-copy-link" onClick={copy}>{copied ? <Check size={15} /> : <Copy size={15} />}<span>{copied ? 'Copied' : 'Copy'}</span></button></div>
      <div className="share-actions">
        {typeof navigator.share === 'function' && <button data-testid="share-native" onClick={nativeShare}><Share2 size={15} /> Share…</button>}
        <a data-testid="share-open-link" href={link} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Open link</a>
        <button data-testid="share-download-pdf" className="share-primary" onClick={downloadPdf} disabled={building}>{building ? <Loader2 className="spin" size={15} /> : <FileDown size={15} />} {building ? 'Creating PDF…' : 'Download PDF spec sheet'}</button>
      </div>
    </DialogContent>
  </Dialog>;
}
